import {
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Typography
} from "@mui/material";
import React, { useState } from "react";
import BreadCrumbsHeader from "../../../components/BreadCrumbsHeader";
import { useQuery } from "@tanstack/react-query";
import UserQuery from "../../../stateQueries/User";
import CustomNoRowsOverlay from "../../../components/CustomNoRowsOverlay";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";

const Subscribers = () => {
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["subscribers", page],
    queryFn: async () => {
      return await UserQuery.CSEQuery.getSubscribers(page);
    },
    refetchOnWindowFocus: false
  });

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await UserQuery.CSEQuery.exportSubscribers();
      const subscribers = response?.subscribers || [];

      // Create worksheet data
      const worksheetData = [
        ["No#", "Email", "Date Subscribed"]
      ];

      subscribers.forEach((subscriber, index) => {
        worksheetData.push([
          index + 1,
          subscriber.email,
          new Date(subscriber.createdAt).toLocaleDateString()
        ]);
      });

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      // Set column widths
      worksheet["!cols"] = [
        { wch: 10 },
        { wch: 40 },
        { wch: 20 }
      ];

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "Subscribers");

      // Generate and download file
      XLSX.writeFile(workbook, `Subscribers_${new Date().toISOString().split("T")[0]}.xlsx`);
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export subscribers");
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return <LinearProgress />;
  }

  const subscribers = data?.data?.subscribers || [];
  const pagination = data?.data?.pagination || {};
  const totalPages = pagination.totalPages || 1;

  return (
    <Stack spacing={2}>
      <BreadCrumbsHeader
        title="Welcome back Tiyisela Themba Makamu"
        menus={[
          { name: "Dashboard", url: "/dashboard" },
          { name: "Website Management", url: "/websiteManagement" },
          { name: "Subscribers", url: "/cse/subscribers" }
        ]}
        sx={{ mb: 2 }}
      />
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        <Typography variant="h6">
          Total Subscribers: {pagination.total || 0}
        </Typography>
        <Button
          variant="contained"
          startIcon={
            isExporting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <DownloadIcon />
            )
          }
          onClick={handleExport}
          disabled={isExporting}
        >
          {isExporting ? "Exporting..." : "Export to Excel"}
        </Button>
      </Stack>
      <TableContainer component={Paper}>
        <Table aria-label="subscribers table">
          <TableHead sx={{ backgroundColor: "background.paper" }}>
            <TableRow>
              <TableCell
                align="center"
                sx={{ fontWeight: "bolder", width: 100 }}
              >
                No#
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: "bolder" }}>
                Email
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: "bolder" }}>
                Date Subscribed
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subscribers.length > 0 ? (
              subscribers.map((subscriber, index) => (
                <TableRow key={subscriber.id}>
                  <TableCell align="center">
                    {(page - 1) * 10 + index + 1}
                  </TableCell>
                  <TableCell align="center">{subscriber.email}</TableCell>
                  <TableCell align="center">
                    {new Date(subscriber.createdAt).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3}>
                  <CustomNoRowsOverlay description="No Subscribers Found" />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {totalPages > 1 && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Stack>
      )}
    </Stack>
  );
};

export default Subscribers;
